import { useQuery } from '@tanstack/react-query';

import CustomerAccountService from 'root/apps/QuoteMachineIntegration/services/CustomerAccountService';
import { CustomerAccount as CreditAccountData } from 'root/apps/QuoteMachineIntegration/state';

import { hasCreditAccountId } from '../utils/creditAccountId';
import { useBalanceDriftDetection } from './useBalanceDriftDetection';

export interface UseCreditAccountBalanceDriftDetectionParams {
  creditAccountID: string;
  currentCreditLimit?: string | null;
  lsrBalanceOwed: number | null;
  lsrDepositBalance: number | null;
  enabled?: boolean;
}

/**
 * Fetches invoicing credit-account balances when `enabled` and a real credit
 * account id is present. Drift *reporting* to the API is gated by
 * `invoicing.api.v1.balance_drift` inside `useBalanceDriftDetection`.
 *
 * Shared by customer profile surfaces (parcel details + WC host) and register sale UI.
 */
export function useCreditAccountBalanceDriftDetection({
  creditAccountID,
  currentCreditLimit,
  lsrBalanceOwed,
  lsrDepositBalance,
  enabled = true,
}: UseCreditAccountBalanceDriftDetectionParams): {
  invoicingData: CreditAccountData | undefined;
} {
  const { data: invoicingData } = useQuery({
    queryKey: ['creditAccountBalance', creditAccountID, String(currentCreditLimit ?? '')],
    queryFn: () =>
      CustomerAccountService.getCustomerAccount(creditAccountID, String(currentCreditLimit ?? '')),
    retry: false,
    cacheTime: 0,
    refetchOnMount: true,
    enabled: enabled && hasCreditAccountId(creditAccountID),
  });

  useBalanceDriftDetection({
    creditAccountID,
    invoicingData,
    lsrBalanceOwed,
    lsrDepositBalance,
  });

  return { invoicingData };
}
